/**
 * The runs roll-up + the one status lexicon (R6), extracted verbatim from
 * CaseFile so the review case body (YAG-440) reads the same runs the generic
 * case file does. No behavior change.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  Run,
  RunStatus,
  WorkItem,
  WorkItemRunListEntry,
  WorkItemRunsView,
} from "@yagni/shared";
import { harperRunStatusLabel } from "../workers/harperRunStatus.js";
import { reconcileLiveRun } from "./workLifecycle.js";

/**
 * The item as the roll-up proves it ({@link reconcileLiveRun}), and the one
 * side effect a stale pointer earns: the Work list re-reads the item, once
 * per pointer, so the phase the record already moved to (a review's `done`)
 * reaches the header on the next paint. A supervised or review run
 * terminalizes in the worker process, whose broadcast reaches this socket
 * only through the backend's best-effort relay, so the roll-up's poll is how
 * the pane learns it ended when a frame is lost; the item itself has no poll,
 * and this is what stands in for one.
 */
export function useLiveItem(
  item: WorkItem,
  runs: ReadonlyArray<Pick<Run, "id" | "status">>,
  runsLoading: boolean,
): WorkItem {
  const live = useMemo(() => reconcileLiveRun(item, runs, runsLoading), [item, runs, runsLoading]);
  // Keyed on the item AND the pointer: the same hook instance can be handed
  // a different item (the pager), and a re-read owed to it must not be
  // suppressed by a pointer an earlier item happened to share.
  const invalidatedFor = useRef<string | null>(null);
  useEffect(() => {
    if (live === item || !item.currentRunId) return;
    const key = `${item.id}:${item.currentRunId}`;
    if (invalidatedFor.current === key) return;
    invalidatedFor.current = key;
    window.dispatchEvent(new CustomEvent("tickets.invalidate"));
  }, [live, item]);
  return live;
}

/* ── runs roll-up + the charged total (always sell-side, never COGS) ── */

/**
 * Liveness floor while a run reads as "running". The surface is otherwise
 * purely event-driven off `runs.updated` WebSocket broadcasts, and a missed
 * event (the socket died in a deploy — the exact moment run statuses change)
 * used to leave the pane saying "running" indefinitely. Polling only while a
 * run wears the running tone means a quiet, settled work item costs nothing.
 *
 * Fifteen seconds, not sixty: a supervised build terminalizes in the Temporal
 * worker process, whose `runs.updated` broadcast reaches this socket only
 * through the backend's best-effort relay (`ws/relay.ts`), so this poll is how
 * a live pane learns the run stopped when that frame is lost. A minute of
 * "Live" over a run that had already failed read as the run still working
 * (2026-09-17).
 */
export const LIVE_RUN_POLL_MS = 15_000;

/**
 * Did any run move between two reads? A run that is new, or whose status is
 * not what the last read said. The first read has nothing to compare against.
 */
function runsMoved(
  before: ReadonlyMap<string, string> | null,
  after: ReadonlyArray<Pick<WorkItemRunListEntry, "id" | "status">>,
): boolean {
  if (!before) return false;
  return after.some((run) => before.get(run.id) !== run.status);
}

/**
 * `onStatusChange` is the safety net for writes this tab did not hear about:
 * a build that stops in the worker process changes the gate and the row as
 * well as the run, and the relay that carries those frames here is best
 * effort. When a poll sees
 * a run move, the caller re-reads the rest. Only on a change, so a quiet poll
 * costs one request and nothing else.
 */
export function useWorkItemRuns(
  workItemId: string,
  onStatusChange?: () => void,
  /** False reads nothing and returns an empty list: a caller that needs the runs only some of the time. */
  enabled = true,
): {
  runs: WorkItemRunListEntry[];
  /** What the workspace was charged across every run; millicents / 100,000 = dollars. */
  totalSellMillicents: number;
  loading: boolean;
  error: Error | null;
  reload: () => void;
  /** Read again in the background: no loading state, and a failure keeps the last read. */
  refresh: () => void;
} {
  const [runs, setRuns] = useState<WorkItemRunListEntry[]>([]);
  const [totalSellMillicents, setTotalSellMillicents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const lastStatuses = useRef<Map<string, string> | null>(null);
  const statusChange = useRef(onStatusChange);
  statusChange.current = onStatusChange;
  useEffect(() => {
    lastStatuses.current = null;
  }, [workItemId]);

  const fetchRuns = useCallback(
    (background: boolean) => {
      if (!enabled) {
        setRuns([]);
        setTotalSellMillicents(0);
        setLoading(false);
        return;
      }
      if (!background) setLoading(true);
      setError(null);
      fetch(`/api/work-items/${encodeURIComponent(workItemId)}/runs-view`, {
        credentials: "include",
      })
        .then((r) => {
          if (!r.ok) throw new Error("runs-view fetch failed");
          return r.json() as Promise<WorkItemRunsView>;
        })
        .then((view) => {
          const list = Array.isArray(view?.runs) ? view.runs : [];
          const moved = background && runsMoved(lastStatuses.current, list);
          lastStatuses.current = new Map(list.map((run) => [run.id, run.status]));
          setRuns(list.slice().sort((a, b) => b.attemptNo - a.attemptNo));
          setTotalSellMillicents(
            typeof view?.totalSellMillicents === "number" ? view.totalSellMillicents : 0,
          );
          if (moved) statusChange.current?.();
        })
        .catch((cause: unknown) => {
          setError(cause instanceof Error ? cause : new Error("Could not load runs."));
          // A failed background poll keeps what the last good read showed; a
          // blip must not blank a list the user is looking at.
          if (!background) {
            setRuns([]);
            setTotalSellMillicents(0);
          }
        })
        .finally(() => {
          if (!background) setLoading(false);
        });
    },
    [workItemId, enabled],
  );

  const reload = useCallback(() => fetchRuns(false), [fetchRuns]);
  const refresh = useCallback(() => fetchRuns(true), [fetchRuns]);

  useEffect(() => {
    fetchRuns(false);
  }, [fetchRuns]);

  useEffect(() => {
    if (!runs.some((run) => RUN_TONE[run.status] === "running")) return;
    const timer = setInterval(() => fetchRuns(true), LIVE_RUN_POLL_MS);
    return () => clearInterval(timer);
  }, [runs, fetchRuns]);

  return { runs, totalSellMillicents, loading, error, reload, refresh };
}

/** Millicents → "$x.xx" (millicents / 100,000 = dollars). Null for nothing to show. */
export function formatMillicents(millicents: number | null | undefined): string | null {
  if (typeof millicents !== "number" || !Number.isFinite(millicents) || millicents <= 0) {
    return null;
  }
  return `$${(millicents / 100_000).toFixed(2)}`;
}

export type RunTone = "running" | "done" | "failed" | "paused" | "muted";

export const RUN_TONE: Record<RunStatus, RunTone> = {
  scoping: "running",
  awaiting_plan_approval: "paused",
  running: "running",
  awaiting_skill_grant: "paused",
  complete: "done",
  partial: "done",
  failed: "failed",
  // Matches the label map below: blocked renders "waiting on you", so the dot
  // wears the paused tone the awaiting_* states wear — a red dot beside
  // "waiting on you" read as a disagreement (operating-picture pass 2026-07-19).
  blocked: "paused",
  // Matches the label map below: dismissed renders the word "failed", so the
  // dot wears the same tone — a muted dot beside the word "failed" read as a
  // disagreement (review 2026-07-03).
  dismissed: "failed",
  // A queued job run that never ran because its work item closed first: not
  // a failure, nothing happened.
  skipped: "muted",
};

// Display lexicon (R6, one status lexicon): ten internal run statuses render as
// six honest words — running / waiting on approval / parked / done / failed /
// skipped (a queued job run whose work item closed before it ran).
// "done" means work landed (complete/partial); "failed" is terminal-without-
// success (failed or dismissed). "waiting on approval" never claims "you":
// every permitted reader sees the row, and only the resolver's header pill may
// say "you" (operating-picture review 2026-07-19). A blocked run splits by its
// park reason: a needs-attention park (budget/deadline) waits on a person; a
// boundary/cap park is a normal pause and reads "parked".
const RUN_STATUS_LABEL: Record<Exclude<RunStatus, "blocked">, string> = {
  scoping: "running",
  awaiting_plan_approval: "waiting on approval",
  running: "running",
  awaiting_skill_grant: "waiting on approval",
  complete: "done",
  partial: "done",
  failed: "failed",
  dismissed: "failed",
  skipped: "skipped",
};

/** Needs-attention park reasons — the only blocked runs that wait on a person. */
const NEEDS_ATTENTION_PARK = new Set(["budget_exceeded", "deadline_expired"]);

export function runIsWaitingOnHuman(run: Run): boolean {
  if (run.status === "awaiting_plan_approval" || run.status === "awaiting_skill_grant") {
    return true;
  }
  return (
    run.status === "blocked" &&
    run.failureReason != null &&
    NEEDS_ATTENTION_PARK.has(run.failureReason)
  );
}

export function runStatusLabel(run: Run): string {
  if (run.status === "blocked") {
    return runIsWaitingOnHuman(run) ? "waiting on approval" : "parked";
  }
  return RUN_STATUS_LABEL[run.status];
}

/**
 * The work item's own word for a run. A build that ended `partial` with a
 * recorded stop (an outcome or a stop reason) stopped; it did not finish, and
 * the Work row already calls it stopped. Only for builds: a review run ends
 * `partial` on every ordinary pass and keeps reading "done".
 */
export function caseRunStatusLabel(
  run: Pick<Run, "status" | "failureReason" | "purpose" | "missionOutcome" | "stopReason"> &
    Partial<Pick<Run, "kind">>,
): string {
  // A review or QA run shares the build's purpose but not its ending: it
  // ends partial with a stop word (superseded, pr_closed) on an ordinary
  // pass, and "stopped" would read as a build that lost its work.
  if (
    run.purpose === "execution" &&
    run.kind !== "review" &&
    run.kind !== "qa" &&
    run.status === "partial" &&
    (run.missionOutcome != null || run.stopReason != null)
  ) {
    return "stopped";
  }
  return runStatusLabel(run as Run);
}

/**
 * The statuses a Line page run can have beyond `runs.status`: a Harper line
 * lists its engine's own runs, whose words come from Harper's run vocabulary
 * (`workers/harperRunStatus.ts`) and whose tones map onto the same five.
 * `held` waits on capacity and resumes on its own, so it wears the paused
 * tone; a run another run caught up is muted, never a failure.
 */
const ENGINE_RUN_TONE: Record<string, RunTone> = {
  in_progress: "running",
  completed: "done",
  nothing_to_file: "muted",
  delivery_failed: "failed",
  held: "paused",
  needs_attention: "failed",
  superseded: "muted",
};

function isRunStatus(status: string): status is RunStatus {
  return Object.prototype.hasOwnProperty.call(RUN_TONE, status);
}

/** The tone of any run a Line page lists, `runs` or Harper's engine. */
export function lineRunTone(status: string): RunTone {
  if (isRunStatus(status)) return RUN_TONE[status];
  return ENGINE_RUN_TONE[status] ?? "muted";
}

/** The word for any run a Line page lists; the one status lexicon, extended. */
export function lineRunLabel(status: string): string {
  if (isRunStatus(status)) return status === "blocked" ? "parked" : RUN_STATUS_LABEL[status];
  return harperRunStatusLabel(status);
}
